import { useEffect, useRef, useState } from "react";
import { Sparkle, PaperPlaneTilt, User } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SESSION = "principal";
const SUGGESTIONS = [
  "Explique-moi l'analyse SWOT",
  "Résume les bases du marketing digital",
  "Donne-moi un plan de révision efficace",
];

export default function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get(`/ai/history/${SESSION}`).then((r) => setMessages(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (msg) => {
    const text = msg || input;
    if (!text.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: SESSION, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto flex h-[80vh] max-w-3xl flex-col" data-testid="ai-page">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Intelligence artificielle</p>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight">
          <Sparkle size={28} weight="fill" className="text-accent" /> Assistant pédagogique
        </h1>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto rounded-xl border border-border bg-card p-5">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Sparkle size={32} weight="duotone" /></div>
            <p className="font-display text-lg font-bold">Comment puis-je vous aider ?</p>
            <p className="mb-6 text-sm text-muted-foreground">Posez une question ou choisissez une suggestion.</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-secondary" data-testid="ai-suggestion">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${m.role === "user" ? "bg-secondary" : "bg-primary text-primary-foreground"}`}>
              {m.role === "user" ? <User size={16} /> : <Sparkle size={16} weight="fill" />}
            </div>
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{m.content}</div>
          </div>
        ))}
        {loading && <p className="text-sm text-muted-foreground">L'assistant réfléchit…</p>}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrivez votre question..." data-testid="ai-input" />
        <Button onClick={() => send()} disabled={loading} className="bg-primary hover:bg-primary/90" data-testid="ai-send"><PaperPlaneTilt size={18} /></Button>
      </div>
    </div>
  );
}
